/**
 * The `@nuxt/image` providers this layer registers, named directly rather than derived from
 * `keyof ConfiguredImageProviders`.
 *
 * That interface is populated by a `declare module '@nuxt/image'` augmentation which Nuxt generates
 * into each project's own `.nuxt`. Module augmentation only lands when the specifier resolves to the
 * same physical copy on both sides, and a layer that extends the design system without depending on
 * `@nuxt/image` itself (`@tapp/layer-storyblok`) has no copy to resolve — so there the interface
 * stays empty and `keyof` collapses to `never`, failing every assignment in this file. Naming the
 * two providers here is also simply more honest: this layer is what registers them.
 */
export type TappImageProvider = 'akamai' | 'storyblok';

const STORYBLOK_ASSET_URL_REGEX = /^.*?\/f\/301263/;

export const STORYBLOK_AKAMAI_PROXY_BASE_URL = 'https://cdn-storyblok.tapp.dpgmedia.cloud';

/**
 * Rewrites a Storyblok CDN asset URL to our Akamai proxy to keep the traffic
 * within the Akamai network and avoid quota limits on the Storyblok CDN.
 * The proxy expects the `/f/<space-id>` prefix to be stripped.
 * Non-Storyblok URLs are returned untouched.
 */
export function mapStoryblokAssetUrlToAkamai(url: string): string {
  return url.replace(STORYBLOK_ASSET_URL_REGEX, STORYBLOK_AKAMAI_PROXY_BASE_URL);
}

const STORYBLOK_ASSET_HOST = 'a.storyblok.com';

/** Whether an asset URL points at Storyblok's own CDN rather than at ours. */
export function isStoryblokAssetUrl(src: string): boolean {
  return src.includes(STORYBLOK_ASSET_HOST);
}

/**
 * Which of our two CDNs serves an asset URL.
 *
 * `<NuxtImg>` falls back to the project's default provider (`akamai`), which leaves a Storyblok URL
 * pointing at a.storyblok.com and hands back the full-resolution source — only the `storyblok`
 * provider rewrites it onto the Akamai proxy and applies the resize modifiers. Components call this
 * on their own `src` rather than taking a `provider` prop: the URL already says which CDN it is on,
 * and both this layer's consumers register the same providers, so the name resolves on either side.
 */
export function getNuxtImageProviderFromSrc(src: string): TappImageProvider {
  if (isStoryblokAssetUrl(src)) {
    return 'storyblok';
  }

  return 'akamai';
}

export function getImageDimensionsFromStoryblokSrc(src: string) {
  try {
    const captured = /.*?\/(?<width>\d*)x(?<height>\d*)\//.exec(src);

    const { width = '', height = '' } = captured?.groups ?? {};

    return { width, height };
  } catch {
    return { width: '', height: '' };
  }
}

const VIDEO_EXTENSION_REGEX = /\.(mp4|webm)(?:$|[?#])/i;

/**
 * Storyblok stores video in the same asset library as images, so a blok field
 * typed as an image can legitimately hold an .mp4 or .webm. Callers use this to
 * decide between <NuxtImg> and <video>, since NuxtImg cannot render video.
 */
export function isVideoAssetUrl(url: string): boolean {
  return VIDEO_EXTENSION_REGEX.test(url);
}

export function getStoryblokImage(imageUrl: string, alt?: string) {
  return {
    src: imageUrl,
    ...getImageDimensionsFromStoryblokSrc(imageUrl),
    alt: alt ?? `Afbeelding van ${imageUrl}`,
    provider: 'storyblok' satisfies TappImageProvider as TappImageProvider,
  };
}

/**
 * The intrinsic dimensions `<NuxtImg>` should resize against, given whatever the consumer knows.
 *
 * An explicit value always wins. Where there is none, a Storyblok URL still carries the asset's own
 * `<width>x<height>` in its path, so we read it back off the `src` rather than making the consumer
 * do it — every caller that had to was running the same two lines. Anything else stays `undefined`:
 * without dimensions the provider has no modifiers to resize against and the CDN hands back the
 * full-resolution source, but inventing a ratio would distort the image, which is worse.
 */
export function resolveImageDimensions(
  src: string,
  width?: number | string,
  height?: number | string,
): { width: number | string | undefined; height: number | string | undefined } {
  if ((width !== undefined && height !== undefined) || !isStoryblokAssetUrl(src)) {
    return { width, height };
  }

  const fromSrc = getImageDimensionsFromStoryblokSrc(src);

  return {
    width: width ?? (Number(fromSrc.width) || undefined),
    height: height ?? (Number(fromSrc.height) || undefined),
  };
}
